import { libraryService } from './libraryService';
import {
  ensureLibraryRoutingReady,
  shouldUseLocalLibraryIpc,
  isLocalCatalogBrowseReady,
  waitForLocalCatalogBrowseReady,
} from './localLibraryIpcGate';
import type { AlbumSummary, ArtistSummary, TrackSummary } from './localLibrary/LocalLibraryAdapter';
import type { CatalogSearchTrack } from './unifiedSearchTypes';
import { normalizeLocalSearchTrack } from './unifiedSearchNormalize';

export interface CatalogSearchArtist {
  id: string;
  name: string;
  source: 'local';
  sourceLabel: string;
}

export interface CatalogSearchAlbum {
  id: string;
  album_key: string;
  title: string;
  artistName: string;
  source: 'local';
  sourceLabel: string;
  year?: number | null;
  track_count?: number;
}

export interface LocalCatalogSearchResult {
  artists: CatalogSearchArtist[];
  albums: CatalogSearchAlbum[];
  tracks: CatalogSearchTrack[];
}

export const EMPTY_LOCAL_CATALOG: LocalCatalogSearchResult = { artists: [], albums: [], tracks: [] };

let _localCatalogReadyForSearch: boolean | null = null;

export function invalidateLocalCatalogForSearchCache(): void {
  _localCatalogReadyForSearch = null;
}

export async function hasLocalCatalogForSearch(force = false): Promise<boolean> {
  if (!shouldUseLocalLibraryIpc()) {
    _localCatalogReadyForSearch = false;
    return false;
  }
  if (!force && _localCatalogReadyForSearch !== null) {
    return _localCatalogReadyForSearch;
  }
  try {
    await ensureLibraryRoutingReady();
    if (isLocalCatalogBrowseReady()) {
      _localCatalogReadyForSearch = true;
      return true;
    }
    const ready = await waitForLocalCatalogBrowseReady(5_000);
    _localCatalogReadyForSearch = ready;
    return ready;
  } catch {
    _localCatalogReadyForSearch = false;
    return false;
  }
}

function normalizeLocalArtist(artist: ArtistSummary): CatalogSearchArtist {
  return {
    id: artist.id,
    name: artist.name || 'Unknown',
    source: 'local',
    sourceLabel: 'Library',
  };
}

function normalizeLocalAlbum(album: AlbumSummary): CatalogSearchAlbum {
  const key = album.album_key || album.id;
  return {
    id: album.id,
    album_key: key,
    title: album.title || 'Unknown',
    artistName: album.artist_name || album.album_artist || '',
    source: 'local',
    sourceLabel: 'Library',
    year: album.year ?? null,
    track_count: album.track_count,
  };
}

function capLocalCatalog(
  artists: CatalogSearchArtist[],
  albums: CatalogSearchAlbum[],
  tracks: CatalogSearchTrack[],
  limit: number,
): LocalCatalogSearchResult {
  return {
    artists: artists.slice(0, limit),
    albums: albums.slice(0, limit),
    tracks: tracks.slice(0, limit),
  };
}

export async function searchLocalCatalog(
  query: string,
  limit = 10,
): Promise<LocalCatalogSearchResult> {
  const trimmed = (query || '').trim();
  if (!trimmed) return { ...EMPTY_LOCAL_CATALOG };
  try {
    const resp = await libraryService.searchLocal(trimmed, limit);
    const artists = (Array.isArray(resp?.artists) ? resp.artists : []).map(normalizeLocalArtist);
    const albums = (Array.isArray(resp?.albums) ? resp.albums : []).map(normalizeLocalAlbum);
    const tracks = (Array.isArray(resp?.tracks) ? resp.tracks : []).map(normalizeLocalSearchTrack);
    return capLocalCatalog(artists, albums, tracks, limit);
  } catch {
    return { ...EMPTY_LOCAL_CATALOG };
  }
}

export function localCatalogResultCount(local: LocalCatalogSearchResult): number {
  return local.artists.length + local.albums.length + local.tracks.length;
}

export function fetchLocalSearchIfConfigured(
  query: string,
  limit: number,
): Promise<LocalCatalogSearchResult> {
  return hasLocalCatalogForSearch()
    .then((hasLocal) => (hasLocal ? searchLocalCatalog(query, limit) : { ...EMPTY_LOCAL_CATALOG }))
    .catch(() => ({ ...EMPTY_LOCAL_CATALOG }));
}
